import { AsyncPipe, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTable, MatLegacyTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import _ from 'lodash';
import { Observable, tap } from 'rxjs';
import { Countertop } from '../../../models/cutting/countertop';
import { CuttingMaterial } from '../../../models/cutting/cutting-material.model';
import { FaceBand } from '../../../models/cutting/cutting-panel-outline.model';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import {
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
} from '../../cutting-material-base-dialog/cutting-material-base-dialog.component';
import { CountertopSettingsStateService } from '../countertop-settings-state.service';

export class FaceBandingBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _FaceBandingMixinBase = mixinDisabled(FaceBandingBase);

@Component({
  selector: 'csl-face-banding',
  templateUrl: 'face-banding.component.html',
  standalone: true,
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  imports: [
    NgIf,
    AsyncPipe,
    NgFor,
    NgSwitch,
    NgSwitchDefault,
    NgSwitchCase,
    MatLegacyButtonModule,
    MatLegacyTableModule,
    MatIconModule,
    MatLegacyTooltipModule,
  ],
})
export class FaceBandingComponent extends _FaceBandingMixinBase implements CanDisable {
  @Input() showQuantityInStock: boolean;

  @ViewChildren(MatLegacyTable) table: QueryList<MatLegacyTable<FaceBand>>;

  countertop: Countertop;

  data$: Observable<any>;
  action$: Observable<CuttingMaterial[]>;

  constructor(
    private dialogs: LegacyDialogsService,
    private countertopSettingsService: CountertopSettingsStateService
  ) {
    super();

    this.data$ = this.countertopSettingsService.countertop$.pipe(
      tap((p: Countertop) => {
        this.countertop = p;

        this.table?.last?.renderRows();
      })
    );
  }

  openMaterialBaseDialog(pos: '+' | '-'): void {
    this.action$ = this.dialogs
      .component<CuttingMaterialBaseDialogData, CuttingMaterial[] | undefined>(
        CuttingMaterialBaseDialogComponent,
        {
          materialsToExclude: [],
          showQuantityInStock: this.showQuantityInStock,
        } as CuttingMaterialBaseDialogData,
        1150,
        {
          panelClass: 'material-base-material-list-dialog',
          disableClose: false,
        }
      )
      .pipe(
        tap((result: CuttingMaterial[] | undefined) => {
          if (result && result.length > 0) {
            result.forEach(m => {
              this.addFaceBand(m, pos);
            });
          }
        })
      );
  }

  private addFaceBand(material: CuttingMaterial, pos: '+' | '-'): void {
    let nextLevel = 0;

    if (this.countertop.outline.faceBanding.length > 0) {
      if (pos === '+' && _.maxBy(this.countertop.outline.faceBanding, b => b.level).level > 0) {
        nextLevel = _.maxBy(this.countertop.outline.faceBanding, b => b.level).level;
      } else if (pos === '-' && _.minBy(this.countertop.outline.faceBanding, b => b.level).level < 0) {
        nextLevel = _.minBy(this.countertop.outline.faceBanding, b => b.level).level;
      }
    }

    if (pos === '+') {
      nextLevel++;
    } else {
      nextLevel--;
    }

    this.countertop.outline.faceBanding.push({
      level: nextLevel,
      material: material,
      order: this.countertop.outline.faceBanding.length + 1,
    } as FaceBand);

    this.countertop.outline.faceBanding.sort((a, b) => b.level - a.level);

    this.countertopSettingsService.updateCountertop(this.countertop);
  }

  deleteBand(band: FaceBand): void {
    _.remove(this.countertop.outline.faceBanding, b => b.level === band.level);

    this.recalculateOrder(this.countertop.outline.faceBanding);

    this.countertopSettingsService.updateCountertop(this.countertop);
  }

  private recalculateOrder(bands: FaceBand[]): void {
    const orders = bands.map(b => b.order).sort((a, b) => b - a);
    let noneVal = 0;

    for (let i = orders.length - 1; i > 0; i--) {
      if (orders[i] - orders[i - 1] > 1) {
        noneVal = orders[i] - 1;
        break;
      }
    }

    bands.forEach(band => {
      if (band.order > noneVal) {
        band.order--;
      }
    });
  }
}
